<script setup lang="ts">
import {onBeforeMount, onBeforeUnmount, onMounted, ref} from 'vue';
import axios from "axios";
import ApiHelper, {API_HELPERS} from "@/helpers/apiHelper.ts";
import {useContestStore} from "@/stores/contest.ts";
import { storeToRefs } from "pinia";
import AuthService from "@/services/authService.ts";
import { VueRecaptcha } from "vue-recaptcha";

const theme = ref<'light' | 'dark'>('light'),
    captchaResponse = ref(),
    responseMessage = ref(''),
    sendingForm = ref(false),
    errors = ref<any>({}),
    backendErrors = ref<any>({}),
    recaptchaRef = ref<InstanceType<typeof VueRecaptcha> | null>(null),
    submissionSection = ref<HTMLElement | null>(null),
    isVisible = ref(false);

let observer: IntersectionObserver | null = null;

const autService = new AuthService();
const contestStore = useContestStore(),
     {contestInfo} = storeToRefs(contestStore);

const verificationCode = ref(''),
    email = ref(''),
    nick = ref(''),
    declaration_of_adulthood = ref(false),
    acceptance_of_the_regulations = ref(false),
    confirmation_of_data_processing_knowledge = ref(false),
    data_processing_consent = ref(false);

const phoneValidation = (phone: string) => {
    const regex = /^(?:(?:(?:(?:\+|00)\d{2})?[ -]?(?:(?:\(0?\d{2}\))|(?:0?\d{2})))?[ -]?(?:\d{3}[- ]?\d{2}[- ]?\d{2}|\d{2}[- ]?\d{2}[- ]?\d{3}|\d{7})|(?:(?:(?:\+|00)\d{2})?[ -]?\d{3}[ -]?\d{3}[ -]?\d{3}))$/;
    return regex.test(phone);
}

const onInvalidSubmit = () => {
    const firstErrorFieldName = Object.keys(errors.value)[0];
    const el = document.querySelector(`[name="${firstErrorFieldName}"]`);
    if (el) {
        el.scrollIntoView({ block: 'center' });
    }
}

const onBackendInvalidSubmit = () => {
    const el = document.querySelector(`form`);
    if (el) {
        el.scrollIntoView({ block: 'center' });
    }
}

const setInvalidField = (name: string) => {
    errors.value[name] = 'To pole jest wymagane';
    onInvalidSubmit();
}

const clearError = (name: string) => {
    if (errors.value[name]) {
        delete errors.value[name];
    }
}

const makeSubmission = async () => {
    errors.value = {};
    if (!nick.value) {
        errors.value['nick'] = 'required';
    }
    if (!verificationCode.value) {
        errors.value['verification_code'] = 'required';
    }
    if (!email.value) {
        errors.value['email'] = 'required';
    }
    if (!declaration_of_adulthood.value) {
        errors.value['declaration_of_adulthood'] = 'required';
    }
    if (!acceptance_of_the_regulations.value) {
        errors.value['acceptance_of_the_regulations'] = 'required';
    }
    if (!confirmation_of_data_processing_knowledge.value) {
        errors.value['confirmation_of_data_processing_knowledge'] = 'required';
    }
    if (!captchaResponse.value) {
        errors.value['captcha'] = 'error';
    }

    if (Object.keys(errors.value).length) {
        onInvalidSubmit();
        return;
    }

    if (!Object.keys(errors.value).length) {
        const data = {
            'g-recaptcha-response': captchaResponse.value,
            'email': email.value,
            'verification_code': verificationCode.value,
            'payload': JSON.stringify({
                'nick': nick.value,
                'declaration_of_adulthood': declaration_of_adulthood.value,
                'acceptance_of_the_regulations': acceptance_of_the_regulations.value,
                'confirmation_of_data_processing_knowledge': confirmation_of_data_processing_knowledge.value,
                'data_processing_consent': data_processing_consent.value,
            }),
            'contest_id': API_HELPERS.contestId,
        };
        sendingForm.value = true;
        const token = await autService.getToken();
        await axios.post(ApiHelper.getSubmissionCreateUrl(), data, {
            headers: {'X-Form-Token': token}
        })
            .then(response => {
                errors.value = {}
                backendErrors.value = {}
                responseMessage.value = '';
                sendingForm.value = false;

                if (response.status == 200 && response.data.submission_id) {
                    responseMessage.value = 'Formularz został wysłany';
                    const submissionId = response.data.submission_id;
                    window.location.href = import.meta.env.VITE_GAME_URL + '?token=' + submissionId;
                } else {
                    responseMessage.value = 'Podczas wysyłania formularza wystąpił błąd.';
                }
            }).catch(error => {
                if (error.response?.status == 422 && error.response?.data?.errors) {
                    if (error.response.data.errors.payload) {
                        responseMessage.value = error.response.data.errors.payload[0];
                    }
                    backendErrors.value = error.response.data.errors;
                    errors.value = {}
                    recaptchaRef.value?.reset();
                    onBackendInvalidSubmit();
                } else if (error.response.status == 412) {
                    captchaResponse.value = '';
                    backendErrors.value['captcha'] = 'error';
                    errors.value = {}
                    recaptchaRef.value?.reset();
                } else {
                    errors.value = {}
                    backendErrors.value = {}
                    responseMessage.value = 'Podczas wysyłania formularza wystąpił błąd.';
                    recaptchaRef.value?.reset();
                }

              sendingForm.value = false;

            });
    }
}

const onCaptchaVerify = (token: string) => {
    captchaResponse.value = token;
}

onBeforeMount(async () => {
    await contestStore.getContestInfo();
})

onMounted(() => {
    if (!submissionSection.value) {
        return;
    }

    observer = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) {
                    return;
                }

                isVisible.value = true;
                observer?.unobserve(entry.target);
            });
        },
        {
            threshold: 0.05,
            rootMargin: "0px 0px -10% 0px"
        }
    );

    observer.observe(submissionSection.value);
});

onBeforeUnmount(() => observer?.disconnect());
</script>

<template>
    <div id="submission" ref="submissionSection" :class="{ 'is-visible': isVisible }" class="text-center py-16 lg:pt-32 max-w-[1080px] m-auto">
        <h2 class="submission-title text-[2.5rem] lg:text-6xl text-default-white text-center pb-4 lg:pb-8">WEŹ UDZIAŁ</h2>
        <div class="w-28 lg:w-52 h-px bg-default-white/70 mx-auto mb-14"></div>
        <template v-if="!contestInfo?.submission_available">
            <div class="text-center text-secondary info-box text-2xl lg:text-3xl">
                <template v-if="contestInfo.submission_ended">
                    Przyjmowanie zgłoszeń w konkursie zostało zakończone.<br/>
                    Dziękujemy za udział!
                </template>
                <template v-else>
                    Przyjmowanie zgłoszeń wkrótce się rozpocznie.
                </template>
            </div>
        </template>
        <template v-else>
            <div v-show="responseMessage" class="message-box text-center text-light-blue text-sm">{{ responseMessage }}</div>
            <form class="container mx-auto pt-4" @submit.prevent="makeSubmission">
                <div class="px-4 grid grid-cols-1 lg:grid-cols-4 place-center gap-4 lg:gap-14 text-left">
                    <div class="form-field lg:col-span-2" style="--field-delay: 0.05s">
                        <label class="pb-1 block text-[1.25rem] lg:text-[1.875rem]" for="nick">Podaj imię lub nick do rankingu</label>
                        <input class="block w-full px-1 py-2.5 pl-2.5"
                               :class="{ 'input-error': (errors && errors.nick) || (backendErrors && backendErrors.nick) }"
                               id="nick"
                               name="nick"
                               v-model="nick"
                               placeholder="Imię lub nick"
                               max="100"
                               required
                               @invalid.prevent="setInvalidField('nick')"
                               @change.prevent="clearError('nick')"
                        />
                        <div class="min-h-[1.75rem] text-right block">
                            <div v-show="(errors && errors.nick) || (backendErrors && backendErrors.nick)" class="text-default-red error-label"><span>Podaj prawidłowy nick</span></div>
                        </div>
                    </div>
                    <div class="form-field lg:col-span-2" style="--field-delay: 0.12s">
                        <label class="pb-1 block text-[1.25rem] lg:text-[1.875rem]" for="code">Wpisz kod z aplikacji Żappka</label>
                        <input class="block w-full px-1 py-2.5 pl-2.5"
                               :class="{ 'input-error': (errors && errors.verification_code) || (backendErrors && backendErrors.verification_code) }"
                               id="code"
                               name="verification_code"
                               v-model="verificationCode"
                               placeholder="Kod z aplikacji Żappka"
                               required
                               @invalid.prevent="setInvalidField('verification_code')"
                               @change.prevent="clearError('verification_code')"
                        />
                        <div class="min-h-[1.75rem] text-right block">
                            <div v-show="(errors && errors.verification_code) || (backendErrors && backendErrors.verification_code)" class="text-default-red error-label"><span>Podaj prawidłowy kod z aplikacji Żappka</span></div>
                        </div>
                    </div>
                    <div class="form-field lg:col-span-4" style="--field-delay: 0.19s">
                        <label class="pb-1 block text-[1.25rem] lg:text-[1.875rem]" for="email">Podaj swój adres e-mail</label>
                        <input class="block w-full px-1 py-2.5 pl-2.5"
                               :class="{ 'input-error': (errors && errors.email) || (backendErrors && backendErrors.email) }"
                               id="email"
                               name="email"
                               v-model="email"
                               type="email"
                               placeholder="Adres e-mail"
                               required
                               @invalid.prevent="setInvalidField('email')"
                               @change.prevent="clearError('email')"
                        />
                        <div class="min-h-[1.75rem] text-right block">
                            <div v-show="(errors && errors.email) || (backendErrors && backendErrors.email)" class="text-default-red error-label"><span>Podaj prawidłowy adres e-mail</span></div>
                        </div>
                    </div>
                </div>

                <div class="checkbox-block flex flex-col gap-4 pt-8 lg:pt-16 px-4 text-base lg:text-xl text-left">
                    <div class="flex gap-2">
                        <input type="checkbox"
                               id="regulation_1"
                               name="declaration_of_adulthood"
                               v-model="declaration_of_adulthood"
                               class="w-4 h-4 shrink-0"
                               :class="{ 'input-error': (errors && errors.declaration_of_adulthood) || (backendErrors && backendErrors.declaration_of_adulthood) }"
                               required
                               @invalid.prevent="setInvalidField('declaration_of_adulthood')"
                               @change.prevent="clearError('declaration_of_adulthood')"
                        />
                        <label for="regulation_1">* Oświadczam, że mam ukończone 18 lat.</label>
                    </div>
                    <div class="flex gap-2">
                        <input type="checkbox"
                               id="regulation_2"
                               name="acceptance_of_the_regulations"
                               v-model="acceptance_of_the_regulations"
                               class="appearance-none w-4 h-4 bg-kozel-white shrink-0"
                               :class="{ 'input-error': (errors && errors.acceptance_of_the_regulations) || (backendErrors && backendErrors.acceptance_of_the_regulations) }"
                               required
                               @invalid.prevent="setInvalidField('acceptance_of_the_regulations')"
                               @change.prevent="clearError('acceptance_of_the_regulations')"
                        />
                        <label for="regulation_2">* Oświadczam, że Regulamin Konkursu „CIAO EMOZIONI 2026” jest mi znany i akceptuję jego treść oraz że spełniam warunki przewidziane w §2 Regulamin Konkursu.</label>
                    </div>
                    <div class="flex gap-2">
                        <input type="checkbox"
                               id="regulation_4"
                               name="confirmation_of_data_processing_knowledge"
                               v-model="confirmation_of_data_processing_knowledge"
                               class="appearance-none w-4 h-4 bg-kozel-white shrink-0"
                               :class="{ 'input-error': (errors && errors.confirmation_of_data_processing_knowledge) || (backendErrors && backendErrors.confirmation_of_data_processing_knowledge) }"
                               required
                               @invalid.prevent="setInvalidField('confirmation_of_data_processing_knowledge')"
                               @change.prevent="clearError('confirmation_of_data_processing_knowledge')"
                        />
                        <label for="regulation_4" >* Oświadczam, iż zostałem poinformowany o zasadach przetwarzania moich danych osobowych, określonych Regulaminem Konkursu „CIAO EMOZIONI 2026”.</label>
                    </div>
                    <div class="flex gap-2">
                        <input type="checkbox"
                               id="regulation_5"
                               v-model="data_processing_consent"
                               class="appearance-none w-4 h-4 bg-kozel-white shrink-0"
                        />
                        <label for="regulation_5" >
                            Wyrażam zgodę na otrzymywanie od Kompanii Piwowarskiej S.A. z siedzibą w Warszawie informacji o produktach, promocjach i wydarzeniach (marketing bezpośredni) za pośrednictwem podanego przeze mnie adresu e-mail. Mam świadomość, że wyrażenie powyższej zgody jest dobrowolne i odbywa się na podstawie art. 398 ustawy Prawo Komunikacji Elektronicznej. Zgoda może zostać wycofana w dowolnym momencie, co nie wpływa na zgodność z prawem komunikacji zrealizowanej przed ich wycofaniem.
                        </label>
                    </div>
                    <div>
                        <div class="regulations-items pt-2 text-[1.25rem] lg:text-[1.875rem]">* Oświadczenie jest obowiązkowe</div>
                        <div
                            v-show="
                                (errors &&
                                    (errors.declaration_of_adulthood ||
                                        errors.acceptance_of_the_regulations ||
                                        errors.confirmation_of_data_processing_knowledge)) ||
                                (backendErrors &&
                                    (backendErrors.declaration_of_adulthood ||
                                        backendErrors.acceptance_of_the_regulations ||
                                        backendErrors.confirmation_of_data_processing_knowledge))
                            "
                            class="text-default-red error-label"
                        >
                            <span>Zaznacz wymagane zgody.</span>
                        </div>
                    </div>
                    <div class="info-clause text-left text-xs lg:text-sm">
                        Informacja o przetwarzaniu danych: Administratorem Twoich danych osobowych jest Kompania Piwowarska S.A. z siedzibą w Warszawie. Twoje dane przetwarzamy w celu przygotowania, wysyłki oraz analizy skuteczności komunikacji marketingowej na podstawie naszego prawnie uzasadnionego interesu (art. 6 ust. 1 lit. f RODO) w postaci promocji naszych marek i utrzymywania kontaktu z osobami zainteresowanymi naszą działalnością. Przysługuje Ci prawo do wglądu w dane, ich poprawiania, żądania usunięcia oraz wniesienia sprzeciwu. Szczegóły znajdziesz w Polityce Prywatności KP.
                    </div>
                </div>

                <div class="regulations-items py-6">
                    <div class="flex flex-col justify-center items-center gap-1">
                        <VueRecaptcha ref="recaptchaRef"
                                      sitekey="6Lf_4wMtAAAAABT8I0utcW_pWezUVMD2k3GHxZ2Q"
                                      @verify="onCaptchaVerify">
                        </VueRecaptcha>
                        <div class="min-h-[1.75rem]">
                            <span
                                v-if="backendErrors['captcha']"
                                class="text-default-red error-label"
                            >
                                <span>Potwierdź, że nie jesteś robotem.</span>
                            </span>
                        </div>
                    </div>
                </div>
                <div class="finish-box relative w-full py-16 md:py-22 lg:py-10">
                    <button class="cta-button block tracking-wider !px-18" :disabled="sendingForm">
                        <span v-if="!sendingForm">WEJDŹ DO GRY</span>
                        <span v-else class="animate__animated animate__fadeIn">WYSYŁANIE...</span>
                    </button>
                </div>
            </form>
        </template>
    </div>
</template>

<style scoped>
.submission-title {
    letter-spacing: 4px;
    opacity: 0;
    transform: translateY(16px);
    transition:
        letter-spacing 0.8s ease,
        transform 0.8s ease,
        opacity 0.8s ease;
}

.form-field {
    opacity: 0;
    transform: translateY(18px);
    transition:
        transform 0.55s ease var(--field-delay),
        opacity 0.55s ease var(--field-delay);
}

.checkbox-block {
    opacity: 0;
    transition: opacity 0.6s ease 0.3s;
}

.cta-button {
    position: relative;
    overflow: hidden;
    transition: transform 0.25s ease, box-shadow 0.25s ease;
}

.cta-button::before {
    content: "";
    position: absolute;
    inset: 0 auto 0 -140%;
    width: 42%;
    background: linear-gradient(115deg, transparent 0%, rgba(255, 255, 255, 0.16) 35%, rgba(255, 255, 255, 0.45) 50%, transparent 100%);
    transform: skewX(-22deg);
    transition: left 0.45s ease;
    pointer-events: none;
}

.cta-button:hover {
    transform: scale(1.03);
    box-shadow: 0 14px 34px rgba(237, 27, 35, 0.28);
}

.cta-button:hover::before {
    left: 135%;
}

.cta-button:active {
    transform: scale(0.97);
    transition-duration: 0.1s;
}

#submission.is-visible .submission-title {
    letter-spacing: 10px;
    opacity: 1;
    transform: translateY(0);
}

#submission.is-visible .form-field {
    opacity: 1;
    transform: translateY(0);
}

#submission.is-visible .checkbox-block {
    opacity: 1;
}

input {
    background: var(--color-default-white);
    font-size: 1.125rem;
    color: var(--color-primary);
    padding-top: 20px;
    padding-bottom: 20px;
}

input::placeholder, label, input, .regulations-items, .message-box, .error-label {
    font-weight: 300;
}

input::placeholder {
    color: var(--color-gray);
}

.regulations-items {
    font-size: 0.8rem;
}

.info-clause {
    font-size: 0.7rem;
    font-weight: 300;
    line-height: 1.3;
    padding-top: 0.5rem;
}

label, .error-label {
    font-weight: 300;
}

.error-label span {
    font-size: 0.6875rem;
    width: auto;
    padding: 0.125rem 0.25rem;
    font-weight: 600;
}

@media screen and (min-width: 1024px) {
    input {
        font-size: 1.25rem;
    }
}
</style>
